import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { jobsApi } from '../../api/jobs';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { useDataRefresh } from '../../contexts/DataRefreshContext';

interface CompleteJobButtonProps {
  jobId: string;
  onCompleted: () => void;
}

const CompleteJobButton = ({ jobId, onCompleted }: CompleteJobButtonProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();
  const { refreshJobs, refreshCandidates } = useDataRefresh();

  const handleComplete = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Call the API to complete the job
      await jobsApi.complete(jobId);
      
      // Refresh jobs and candidates data globally
      refreshJobs();
      refreshCandidates(); // Refresh candidates since they're related to the job
      
      // Call onCompleted to update parent component
      onCompleted();
      
      // Hide confirmation dialog
      setShowConfirmation(false);
    } catch (err) {
      console.error('Failed to complete job:', err);
      setError(t('dashboard.completeJob.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        className="flex items-center justify-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded-lg transition-colors"
        onClick={(e) => {
          e.stopPropagation(); // Prevent clicking through to the card
          setShowConfirmation(true);
        }}
        disabled={isLoading}
      >
        {isLoading ? (
          <div className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
        ) : (
          <CheckCircle2 className="w-3 h-3" />
        )}
        <span>{t('dashboard.completeJob.buttonText')}</span>
      </button>
      
      {showConfirmation && (
        <ConfirmationDialog
          title={t('dashboard.completeJob.title')}
          message={t('dashboard.completeJob.message')}
          onConfirm={handleComplete}
          onCancel={() => setShowConfirmation(false)}
        />
      )}
      
      {error && (
        <div className="text-red-600 text-xs mt-1">{error}</div>
      )}
    </>
  );
};

export default CompleteJobButton;